import React, { useId, useState } from 'react';
import type { Feature } from 'geojson';
import { DebounceInput } from 'react-debounce-input';

const LATITUDE_MIN = -90;
const LATITUDE_MAX = 90;
const LONGITUDE_MIN = -180;
const LONGITUDE_MAX = 180;
const DEBOUNCE_MS = 300;

const deriveLatLngStrings = (
  centroid: Feature | undefined
): { lat: string; lng: string } => {
  if (centroid?.geometry?.type !== 'Point') return { lat: '', lng: '' };
  const [lng, lat] = centroid.geometry.coordinates as [number, number];
  return { lat: lat.toString(), lng: lng.toString() };
};

const isInRange = (n: number, min: number, max: number): boolean =>
  !isNaN(n) && n >= min && n <= max;

export type LatLngCoords = { latitude: number; longitude: number };

export type LatLngInputPairProps = {
  centroid: Feature | undefined;
  onChange: (coords: LatLngCoords) => void;
  readOnly?: boolean;
};

const LatLngInputPairInner: React.FC<LatLngInputPairProps> = ({
  centroid,
  onChange,
  readOnly = false,
}) => {
  const baseId = useId();
  const latId = `${baseId}-lat`;
  const lngId = `${baseId}-lng`;
  const latErrorId = `${latId}-error`;
  const lngErrorId = `${lngId}-error`;

  const { lat: initialLat, lng: initialLng } = deriveLatLngStrings(centroid);
  const [localLat, setLocalLat] = useState<string>(initialLat);
  const [localLng, setLocalLng] = useState<string>(initialLng);

  const latNum = parseFloat(localLat);
  const lngNum = parseFloat(localLng);
  const latValid = isInRange(latNum, LATITUDE_MIN, LATITUDE_MAX);
  const lngValid = isInRange(lngNum, LONGITUDE_MIN, LONGITUDE_MAX);
  const latError =
    localLat !== '' && !latValid
      ? `Latitude should be between ${LATITUDE_MIN} and ${LATITUDE_MAX}`
      : null;
  const lngError =
    localLng !== '' && !lngValid
      ? `Longitude should be between ${LONGITUDE_MIN} and ${LONGITUDE_MAX}`
      : null;

  const handleLatChange = (value: string) => {
    setLocalLat(value);
    const next = parseFloat(value);
    if (isInRange(next, LATITUDE_MIN, LATITUDE_MAX) && lngValid) {
      onChange({ latitude: next, longitude: lngNum });
    }
  };

  const handleLngChange = (value: string) => {
    setLocalLng(value);
    const next = parseFloat(value);
    if (isInRange(next, LONGITUDE_MIN, LONGITUDE_MAX) && latValid) {
      onChange({ latitude: latNum, longitude: next });
    }
  };

  return (
    <div className="row mb-3">
      <div className="col-md-6">
        <label htmlFor={latId} className="form-label">
          Lat
        </label>
        <DebounceInput
          id={latId}
          name="lat"
          type="number"
          step="any"
          min={LATITUDE_MIN}
          max={LATITUDE_MAX}
          placeholder="Lat"
          className={`form-control${latError ? ' is-invalid' : ''}`}
          debounceTimeout={DEBOUNCE_MS}
          value={localLat}
          onChange={(e) => handleLatChange(e.target.value)}
          disabled={readOnly}
          data-testid="lat-input"
          aria-invalid={latError !== null}
          aria-describedby={latError ? latErrorId : undefined}
        />
        {latError && (
          <div id={latErrorId} className="invalid-feedback d-block">
            {latError}
          </div>
        )}
      </div>
      <div className="col-md-6">
        <label htmlFor={lngId} className="form-label">
          Lng
        </label>
        <DebounceInput
          id={lngId}
          name="lng"
          type="number"
          step="any"
          min={LONGITUDE_MIN}
          max={LONGITUDE_MAX}
          placeholder="Lng"
          className={`form-control${lngError ? ' is-invalid' : ''}`}
          debounceTimeout={DEBOUNCE_MS}
          value={localLng}
          onChange={(e) => handleLngChange(e.target.value)}
          disabled={readOnly}
          data-testid="lng-input"
          aria-invalid={lngError !== null}
          aria-describedby={lngError ? lngErrorId : undefined}
        />
        {lngError && (
          <div id={lngErrorId} className="invalid-feedback d-block">
            {lngError}
          </div>
        )}
      </div>
    </div>
  );
};

export const LatLngInputPair: React.FC<LatLngInputPairProps> = (props) => {
  const { lat, lng } = deriveLatLngStrings(props.centroid);
  return <LatLngInputPairInner key={`${lat},${lng}`} {...props} />;
};
